import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  AlertTitle,
  IconButton,
  Snackbar,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { isDemoEnv, isProdEnv, allowApiUsers } from '../utils/environment';

const isDemo = isDemoEnv;

// App URLs based on environment
const getAppUrl = () => {
  if (isDemo) return 'https://demo.d2rh64qlfnffkx.amplifyapp.com/';
  if (isProdEnv) return 'https://app.billivo.com/';
  return 'https://dev.billivo.com/'; // dev environment
};

type LinkProps = {
  children?: React.ReactNode;
};

const AppLink: React.FC<LinkProps> = ({ children }) => (
  <a href={getAppUrl()} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

interface ApiCredentials {
  username: string;
  email: string;
  password: string;
  clientId?: string;
  clientSecret?: string;
}

interface LocationState {
  apiCredentials?: ApiCredentials;
}

const RegistrationSuccess: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const apiCredentials = state?.apiCredentials;

  const [copiedSnackbarOpen, setCopiedSnackbarOpen] = useState(false);
  const [copiedField, setCopiedField] = useState('');

  const handleCopy = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setCopiedSnackbarOpen(true);
    } catch (error) {
      console.error('Error copying text:', error);
    }
  };

  const handleCloseSnackbar = () => {
    setCopiedSnackbarOpen(false);
  };

  const showApiCredentials = allowApiUsers && apiCredentials;

  return (
    <Container maxWidth={showApiCredentials ? 'md' : 'sm'}>
      <Paper elevation={3} sx={{ p: 4, mt: 4, textAlign: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
          <Typography variant="h4" gutterBottom>
            {t('registrationSuccess.title')}
          </Typography>
        </Box>

        {showApiCredentials && (
          <Box sx={{ mt: 3, mb: 3 }}>
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ textAlign: 'left' }}>
              <AlertTitle>
                <strong>{t('registrationSuccess.apiCredentials.title')}</strong>
              </AlertTitle>

              <Typography variant="body2" sx={{ mb: 2 }}>
                <Trans
                  i18nKey="registrationSuccess.apiCredentials.warning"
                  components={{
                    strong: <strong />,
                  }}
                />
              </Typography>

              <Box
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  p: 2,
                  borderRadius: 1,
                  mt: 2,
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {t('registrationSuccess.apiCredentials.emailLabel')}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleCopy(apiCredentials.email, t('registrationSuccess.snackbar.email'))
                      }
                      sx={{ ml: 1 }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      backgroundColor: 'white',
                      p: 1,
                      borderRadius: 1,
                      wordBreak: 'break-all',
                    }}
                  >
                    {apiCredentials.email}
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {t('registrationSuccess.apiCredentials.passwordLabel')}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleCopy(
                          apiCredentials.password,
                          t('registrationSuccess.snackbar.password'),
                        )
                      }
                      sx={{ ml: 1 }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      backgroundColor: 'white',
                      p: 1,
                      borderRadius: 1,
                      wordBreak: 'break-all',
                    }}
                  >
                    {apiCredentials.password}
                  </Typography>
                </Box>
              </Box>
            </Alert>
          </Box>
        )}

        <Typography variant="body1">
          {isDemo ? (
            <Trans
              i18nKey="registrationSuccess.descriptionDemo"
              components={{
                link: <AppLink />,
              }}
            />
          ) : (
            t('registrationSuccess.descriptionStandard')
          )}
        </Typography>
      </Paper>

      <Snackbar
        open={copiedSnackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={t('registrationSuccess.snackbar.copied', { field: copiedField })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default RegistrationSuccess;
